/**
 * The framing dialog (plan F2): where the subject sits in the featured photo, which slots crop it,
 * and a trim of the original; for a body photo, the trim alone.
 *
 * Every coordinate here is a fraction of the ROOT original (what getFraming returns), whichever
 * asset the piece shows. The previews draw the original and cut it in CSS, so a trim is seen
 * before it is made.
 */
import { useEffect, useRef, useState, type CSSProperties, type KeyboardEvent, type PointerEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { PieceDetail } from '../api'
import { describeError } from '../../lib/describeError'
import { safeHref } from '../../lib/html'
import type { FigureAttrs } from './figure'
import {
  FIT_RATIOS, PREVIEW_SLOTS, boxOutOfCrop, cleanFit, effectiveFit, featuredSaveBody, objectPosition, pointIntoCrop,
  ratioLockBox, resetFraming, restoreOriginalBody, sameBox, slotWarnings, windowFor, withSlotFit, type Box, type Fit,
} from './framing'
import { cropFigure, getFraming, saveFeaturedFraming, suggestFraming, type Framing } from './framingApi'
import { SAVE_FIRST } from './pieceHelpers'
import { btn, btnPrimary, btnSmall, errorText, focus } from './ui'

/** A body figure is named by its photo and, when known, its position in the document (two
 *  figures can show the same photo). */
export type FramingTarget = { kind: 'featured' } | { kind: 'figure'; assetId: number; pos?: number }

/** A letterboxed slot's bands: a light neutral like the reader's page. */
export const LETTERBOX_BG = '#f3eee4'
const WHOLE: Box = [0, 0, 1, 1]
const MIN_TRIM = 0.05
const pctOf = (v: number) => `${v * 100}%`
const clamp = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), hi)

const asBox = (b: number[] | null | undefined): Box | null =>
  b && b.length === 4 && b.every((v) => Number.isFinite(v)) ? [b[0], b[1], b[2], b[3]] : null

/** The box as fractions of `crop`, cut to it. */
function boxIntoCrop(box: Box, crop: Box): Box {
  const [x0, y0] = pointIntoCrop([box[0], box[1]], crop)
  const [x1, y1] = pointIntoCrop([box[2], box[3]], crop)
  return [x0, y0, x1, y1]
}

/** The `box` of the image (fractions) filling its positioned parent. */
function CropFill({ src, box }: { src: string; box: Box }) {
  const bw = box[2] - box[0] || 1
  const bh = box[3] - box[1] || 1
  const style: CSSProperties = {
    position: 'absolute', maxWidth: 'none',
    width: pctOf(1 / bw), height: pctOf(1 / bh), left: pctOf(-box[0] / bw), top: pctOf(-box[1] / bh),
  }
  return <img src={src} alt="" draggable={false} style={style} />
}

/** A trim seen in CSS alone: a container at the box's aspect, the image scaled and offset in it. */
export function CropPreview({ src, box, width, height }: { src: string; box: Box; width: number | null; height: number | null }) {
  const aspect = ((box[2] - box[0]) * (width || 1)) / ((box[3] - box[1]) * (height || 1))
  return (
    <div className="relative w-full overflow-hidden rounded" style={{ aspectRatio: String(aspect || 1) }}>
      <CropFill src={src} box={box} />
    </div>
  )
}

/**
 * Each place the featured photo is cropped, at its ratio, as the reader draws it: cover at the
 * focal point, or whole on a light band when the slot is letterboxed. `fit` is the piece's own;
 * without one the slots letterbox automatically (framing.py auto_fit), judged in the photo shown
 * (the pending trim, when there is one). `width`, `height`, `focal`, `box` and `crop` (a trim not
 * yet made) are all in fractions of `src`.
 */
export function SlotPreviews({ src, width, height, focal, box, fit, crop, onFit }: {
  src: string
  width: number | null
  height: number | null
  focal: [number, number] | null
  box: Box | null
  fit: Fit | null
  crop?: Box | null
  /** "Show whole" or "Crop to fill" on one slot: the fit shown, with that slot changed. */
  onFit?: (next: Fit) => void
}) {
  const trim = crop ?? null
  const t = trim ?? WHOLE
  // Fractions of a photo without its size are judged as if square.
  const sw = (width || 1) * (t[2] - t[0])
  const sh = (height || 1) * (t[3] - t[1])
  const f = focal && trim ? pointIntoCrop(focal, trim) : focal
  const b = box && trim ? boxIntoCrop(box, trim) : box
  // Without a size there is nothing to judge an automatic letterbox by (the server has none either).
  const shownFit = effectiveFit(fit, width && height ? sw : null, sh, b)
  const warnings = slotWarnings(sw, sh, f, b, shownFit)
  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-3">
        {PREVIEW_SLOTS.map((s) => {
          const ratio = FIT_RATIOS[s.fitKey]
          const contain = shownFit?.[s.fitKey] === 'contain'
          const warning = warnings[s.fitKey]
          return (
            <figure key={s.key} className="m-0 flex flex-col gap-1">
              <figcaption className="font-ui text-[12px] font-semibold text-paper-dim">{s.label}</figcaption>
              <div className="relative w-full overflow-hidden rounded" style={{ aspectRatio: String(ratio), background: contain ? LETTERBOX_BG : undefined }}>
                {!trim
                  ? <img src={src} alt="" draggable={false}
                         className={`absolute inset-0 h-full w-full ${contain ? 'object-contain' : 'object-cover'}`}
                         style={contain ? undefined : { objectPosition: objectPosition(f) }} />
                  : contain
                    ? <ContainedTrim src={src} trim={trim} aspect={sw / sh} ratio={ratio} />
                    : <CropFill src={src} box={boxOutOfCrop(windowFor(sw, sh, ratio, f ?? [0.5, 0.5]), trim)} />}
              </div>
              {warning === 'cannot_fit' && (
                <div className="flex flex-col items-start gap-1">
                  <p role="status" className="m-0 font-ui text-[12px] font-semibold text-amber-deep">The subject can't fit this crop.</p>
                  {onFit && (
                    <button type="button" className={btnSmall} aria-label={`Show whole (letterbox): ${s.label}`} onClick={() => onFit(withSlotFit(shownFit, s.fitKey, 'contain'))}>
                      Show whole (letterbox)
                    </button>
                  )}
                </div>
              )}
              {warning === 'cuts_subject' && <p role="status" className="m-0 font-ui text-[12px] font-semibold text-amber-deep">This crop cuts the subject.</p>}
              {contain && onFit && (
                <button type="button" className={`${btnSmall} self-start`} aria-label={`Crop to fill: ${s.label}`} onClick={() => onFit(withSlotFit(shownFit, s.fitKey, 'cover'))}>
                  Crop to fill
                </button>
              )}
            </figure>
          )
        })}
      </div>
      <p className="m-0 font-ui text-[12px] text-paper-faint">Shown whole: piece page, member article, body photos, emails</p>
    </div>
  )
}

/** A trim shown whole inside a slot of another ratio, centred on the band. */
function ContainedTrim({ src, trim, aspect, ratio }: { src: string; trim: Box; aspect: number; ratio: number }) {
  const style: CSSProperties = aspect > ratio
    ? { position: 'absolute', left: 0, width: '100%', height: pctOf(ratio / aspect), top: pctOf((1 - ratio / aspect) / 2) }
    : { position: 'absolute', top: 0, height: '100%', width: pctOf(aspect / ratio), left: pctOf((1 - aspect / ratio) / 2) }
  return <div className="overflow-hidden" style={style}><CropFill src={src} box={trim} /></div>
}

type Props = {
  detail: PieceDetail
  target: FramingTarget
  /** False refuses the write: edits are waiting that could not be saved. */
  beforeWrite: () => Promise<boolean>
  onDetail: (d: PieceDetail) => void
  /** A body photo's trim (or its original back) in place of the figure at `pos` showing
   *  `oldAssetId`; false when it is gone. */
  onReplaceFigure: (oldAssetId: number, attrs: FigureAttrs, pos?: number) => boolean | void
  onClose: () => void
}

const framingKey = (id: number, target: FramingTarget) => ['piece-framing', id, target.kind === 'figure' ? target.assetId : 'featured'] as const

export function FramingDialog({ detail, target, beforeWrite, onDetail, onReplaceFigure, onClose }: Props) {
  const qc = useQueryClient()
  const key = framingKey(detail.id, target)
  const q = useQuery({
    queryKey: key,
    queryFn: () => getFraming(detail.id, target.kind === 'figure' ? target.assetId : undefined),
  })
  const suggest = useMutation({
    mutationFn: (assetId: number) => suggestFraming(detail.id, assetId),
    onSuccess: (r) => qc.setQueryData(key, r),
  })
  // No suggestion yet: ask the model once, while the dialog is open.
  const asked = useRef(false)
  useEffect(() => {
    if (q.data && q.data.suggestion == null && !asked.current) {
      asked.current = true
      suggest.mutate(q.data.asset_id)
    }
  }, [q.data, suggest])

  const title = target.kind === 'featured' ? 'Framing' : 'Trim photo'
  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-ink-900/70 p-4 font-ui" role="presentation" onClick={onClose}>
      <div
        role="dialog" aria-modal="true" aria-labelledby="framing-title"
        className="my-6 flex w-full max-w-[1100px] flex-col gap-4 rounded-xl border border-ink-500 bg-ink-700 p-5 text-paper shadow-xl"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => { if (e.key === 'Escape') onClose() }}
      >
        <div className="flex items-center justify-between gap-2">
          <h2 id="framing-title" className="m-0 text-[17px] font-semibold">{title}</h2>
          <button type="button" className={btnSmall} onClick={onClose} autoFocus>Close</button>
        </div>
        {q.isPending && <p role="status" className="m-0 text-[13px] text-paper-dim">Loading the photograph…</p>}
        {q.isError && <p className={errorText}>{describeError(q.error)}</p>}
        {suggest.isPending && <p role="status" className="m-0 text-[13px] text-paper-dim">Finding the subject…</p>}
        {q.data && (
          <FramingEditor
            // A save starts the editor again from what the server now holds; a suggestion does not.
            key={`${q.data.asset_id}:${JSON.stringify(q.data.piece)}:${JSON.stringify(q.data.crop_box)}`}
            detail={detail} target={target} data={q.data} beforeWrite={beforeWrite} onDetail={onDetail}
            onReplaceFigure={onReplaceFigure} onClose={onClose}
            onSaved={() => qc.invalidateQueries({ queryKey: key })}
          />
        )}
      </div>
    </div>
  )
}

type Drag =
  | { kind: 'focal' }
  | { kind: 'move'; from: [number, number]; box: Box }
  | { kind: 'corner'; anchor: [number, number] }

const RATIOS: { label: string; value: number | null }[] = [
  { label: 'Free', value: null }, { label: '16:9', value: 16 / 9 }, { label: '3:2', value: 3 / 2 },
]

function FramingEditor({ detail, target, data, beforeWrite, onDetail, onReplaceFigure, onClose, onSaved }: Props & {
  data: Framing
  onSaved: () => void
}) {
  const featured = target.kind === 'featured'
  const src = safeHref(data.original.src) ?? ''
  const W = data.original.width || 1
  const H = data.original.height || 1
  const savedCrop = asBox(data.crop_box)
  const pieceBox = asBox(data.piece?.subject_box)
  const box = pieceBox ?? asBox(data.suggestion?.subject_box)
  const suggestedFocal: [number, number] | null =
    data.suggestion?.focal_x != null && data.suggestion?.focal_y != null ? [data.suggestion.focal_x, data.suggestion.focal_y] : null

  const [focal, setFocal] = useState<[number, number] | null>(
    data.piece?.focal_x != null && data.piece?.focal_y != null ? [data.piece.focal_x, data.piece.focal_y] : null)
  // The piece's own fit; null is the automatic letterbox, which the previews work out.
  const [fit, setFit] = useState<Fit | null>(() => cleanFit(data.piece?.fit))
  const [trimOn, setTrimOn] = useState(savedCrop != null)
  const [trim, setTrim] = useState<Box>(savedCrop ?? [0.05, 0.05, 0.95, 0.95])
  const [ratio, setRatio] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)

  const crop = trimOn ? trim : null
  /** A point kept inside the trim, when there is one. */
  const inTrim = (p: [number, number]): [number, number] =>
    crop ? [clamp(p[0], crop[0], crop[2]), clamp(p[1], crop[1], crop[3])] : [clamp(p[0], 0, 1), clamp(p[1], 0, 1)]
  const shownFocal = focal ?? suggestedFocal
  const dot = shownFocal ? inTrim(shownFocal) : [0.5, 0.5] as [number, number]

  const stage = useRef<HTMLDivElement>(null)
  const drag = useRef<Drag | null>(null)
  const at = (e: PointerEvent): [number, number] => {
    const r = stage.current!.getBoundingClientRect()
    return [clamp((e.clientX - r.left) / r.width, 0, 1), clamp((e.clientY - r.top) / r.height, 0, 1)]
  }
  const begin = (e: PointerEvent, d: Drag) => {
    e.preventDefault()
    e.stopPropagation()
    stage.current?.setPointerCapture(e.pointerId)
    drag.current = d
  }
  const locked = (b: Box) => ratioLockBox(b, ratio, W, H)
  const onMove = (e: PointerEvent) => {
    const d = drag.current
    if (!d) return
    const p = at(e)
    if (d.kind === 'focal') setFocal(inTrim(p))
    else if (d.kind === 'move') {
      const [bw, bh] = [d.box[2] - d.box[0], d.box[3] - d.box[1]]
      const x0 = clamp(d.box[0] + p[0] - d.from[0], 0, 1 - bw)
      const y0 = clamp(d.box[1] + p[1] - d.from[1], 0, 1 - bh)
      setTrim([x0, y0, x0 + bw, y0 + bh])
    } else {
      const [ax, ay] = d.anchor
      let dw = Math.max(Math.abs(p[0] - ax), MIN_TRIM) * W
      let dh = Math.max(Math.abs(p[1] - ay), MIN_TRIM) * H
      if (ratio != null) { if (dw / dh > ratio) dw = dh * ratio; else dh = dw / ratio }
      const x1 = ax + Math.sign(p[0] - ax || 1) * dw / W
      const y1 = ay + Math.sign(p[1] - ay || 1) * dh / H
      setTrim(locked([Math.min(ax, x1), Math.min(ay, y1), Math.max(ax, x1), Math.max(ay, y1)]))
    }
  }
  const end = (e: PointerEvent) => {
    drag.current = null
    if (stage.current?.hasPointerCapture(e.pointerId)) stage.current.releasePointerCapture(e.pointerId)
  }

  const step = (e: KeyboardEvent) => (e.shiftKey ? 0.05 : 0.01)
  const arrow = (e: KeyboardEvent): [number, number] | null => {
    const s = step(e)
    switch (e.key) {
      case 'ArrowLeft': return [-s, 0]
      case 'ArrowRight': return [s, 0]
      case 'ArrowUp': return [0, -s]
      case 'ArrowDown': return [0, s]
      default: return null
    }
  }
  const onDotKey = (e: KeyboardEvent) => {
    const d = arrow(e)
    if (!d) return
    e.preventDefault()
    setFocal(inTrim([dot[0] + d[0], dot[1] + d[1]]))
  }
  /** Arrows move the trim; Alt and the arrows move its right and bottom edges. */
  const onTrimKey = (e: KeyboardEvent) => {
    const d = arrow(e)
    if (!d) return
    e.preventDefault()
    const [x0, y0, x1, y1] = trim
    if (!e.altKey) {
      const dx = clamp(d[0], -x0, 1 - x1)
      const dy = clamp(d[1], -y0, 1 - y1)
      setTrim([x0 + dx, y0 + dy, x1 + dx, y1 + dy])
      return
    }
    let nx1 = clamp(x1 + d[0], x0 + MIN_TRIM, 1)
    let ny1 = clamp(y1 + d[1], y0 + MIN_TRIM, 1)
    if (ratio != null) {
      if (d[0] !== 0) ny1 = y0 + ((nx1 - x0) * W) / ratio / H
      else nx1 = x0 + ((ny1 - y0) * H * ratio) / W
    }
    setTrim(locked([x0, y0, nx1, ny1]))
  }
  const chooseRatio = (r: number | null) => { setRatio(r); setTrim((b) => ratioLockBox(b, r, W, H)) }

  /** Save edits first, then write; any failure shows in the dialog. */
  const write = async <T,>(fn: () => Promise<T>): Promise<T> => {
    setError(null)
    if (!(await beforeWrite())) throw new Error(SAVE_FIRST)
    return fn()
  }
  const state = { focal, pieceBox, fit, trimOn, trim, savedCrop }
  const figureAttrs = (a: { asset_id: number; src: string; width: number | null; height: number | null; alt?: string | null; caption?: string | null }): FigureAttrs =>
    ({ assetId: a.asset_id, src: a.src, width: a.width, height: a.height, alt: a.alt ?? '', caption: a.caption ?? '' })
  const replace = (a: FigureAttrs) => {
    if (target.kind !== 'figure') return
    if (onReplaceFigure(target.assetId, a, target.pos) === false) throw new Error('That photo is no longer in the text.')
    onClose()
  }
  const restoreFigure = () => replace(figureAttrs(data.original))

  const save = useMutation({
    mutationFn: async () => {
      if (featured) {
        const body = featuredSaveBody(state)
        onDetail((await write(() => saveFeaturedFraming(detail.id, body))) as PieceDetail)
        onClose()
        return
      }
      if (trimOn && !sameBox(trim, savedCrop)) replace(figureAttrs(await write(() => cropFigure(detail.id, data.asset_id, trim))))
      else if (!trimOn && savedCrop) restoreFigure()
      else onClose()
    },
    onError: (e) => setError(describeError(e)),
  })
  const reset = useMutation({
    mutationFn: () => write(() => saveFeaturedFraming(detail.id, { focal_x: null, focal_y: null, subject_box: null, fit: null })),
    onSuccess: (r) => {
      // Nothing saved means nothing changes on the server, so the editor is not remounted: clear it here.
      const after = resetFraming(state)
      setFocal(after.focal)
      setFit(after.fit)
      onDetail(r as PieceDetail)
      onSaved()
    },
    onError: (e) => setError(describeError(e)),
  })
  const restore = useMutation({
    mutationFn: async () => {
      if (!featured) { restoreFigure(); return }
      const r = await write(() => saveFeaturedFraming(detail.id, restoreOriginalBody(state)))
      onDetail(r as PieceDetail)
      onSaved()
    },
    onError: (e) => setError(describeError(e)),
  })
  const busy = save.isPending || reset.isPending || restore.isPending

  const rect = (b: Box): CSSProperties => ({ left: pctOf(b[0]), top: pctOf(b[1]), width: pctOf(b[2] - b[0]), height: pctOf(b[3] - b[1]) })
  const corners: { label: string; at: (b: Box) => [number, number]; anchor: (b: Box) => [number, number]; cursor: string }[] = [
    { label: 'top left', at: (b) => [b[0], b[1]], anchor: (b) => [b[2], b[3]], cursor: 'nwse-resize' },
    { label: 'top right', at: (b) => [b[2], b[1]], anchor: (b) => [b[0], b[3]], cursor: 'nesw-resize' },
    { label: 'bottom left', at: (b) => [b[0], b[3]], anchor: (b) => [b[2], b[1]], cursor: 'nesw-resize' },
    { label: 'bottom right', at: (b) => [b[2], b[3]], anchor: (b) => [b[0], b[1]], cursor: 'nwse-resize' },
  ]
  const pct = (v: number) => Math.round(v * 100)

  return (
    <>
      <div className="grid gap-5 md:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <div className="flex flex-col gap-3">
          <div
            ref={stage}
            className="relative w-full touch-none select-none overflow-hidden rounded bg-ink-900"
            style={{ aspectRatio: String(W / H), cursor: featured ? 'crosshair' : undefined }}
            onPointerDown={(e) => { if (featured) { begin(e, { kind: 'focal' }); setFocal(inTrim(at(e))) } }}
            onPointerMove={onMove} onPointerUp={end} onPointerCancel={end}
          >
            <img src={src} alt="The original photograph" draggable={false} className="absolute inset-0 h-full w-full" />
            {box && <div aria-hidden="true" className="pointer-events-none absolute border border-dashed border-white/50" style={rect(box)} />}
            {trimOn && (
              <div
                tabIndex={0} role="group"
                aria-label={`Trim, ${pct(trim[0])}% to ${pct(trim[2])}% across, ${pct(trim[1])}% to ${pct(trim[3])}% down. Arrow keys move it; Alt and the arrow keys resize it.`}
                className={`absolute cursor-move border-2 border-white ${focus}`}
                style={{ ...rect(trim), boxShadow: '0 0 0 9999px rgb(0 0 0 / 0.55)' }}
                onPointerDown={(e) => begin(e, { kind: 'move', from: at(e), box: trim })}
                onKeyDown={onTrimKey}
              >
                {corners.map((c) => {
                  const [x, y] = c.at(trim)
                  return (
                    <span key={c.label} aria-hidden="true"
                          className="absolute h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-sm border border-ink-900 bg-white"
                          style={{ left: pctOf((x - trim[0]) / (trim[2] - trim[0])), top: pctOf((y - trim[1]) / (trim[3] - trim[1])), cursor: c.cursor }}
                          onPointerDown={(e) => begin(e, { kind: 'corner', anchor: c.anchor(trim) })} />
                  )
                })}
              </div>
            )}
            {featured && (
              <button
                type="button"
                aria-label={`Focal point, ${pct(dot[0])}% across and ${pct(dot[1])}% down${focal ? '' : ' (suggested)'}. Arrow keys move it.`}
                className={`absolute h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-amber-glow shadow ${focus}`}
                style={{ left: pctOf(dot[0]), top: pctOf(dot[1]), cursor: 'grab' }}
                onPointerDown={(e) => begin(e, { kind: 'focal' })}
                onKeyDown={onDotKey}
              />
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" className={btnSmall} aria-pressed={trimOn} onClick={() => setTrimOn((v) => !v)}>
              {trimOn ? 'Trim: on' : 'Trim'}
            </button>
            {trimOn && (
              <div role="group" aria-label="Trim shape" className="flex gap-1">
                {RATIOS.map((r) => (
                  <button key={r.label} type="button" aria-pressed={ratio === r.value} onClick={() => chooseRatio(r.value)}
                          className={`${btnSmall} ${ratio === r.value ? 'border-amber-glow' : ''}`}>
                    {r.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <p className="m-0 text-[12px] text-paper-faint">
            {featured
              ? 'Drag the dot to the subject; every crop centres on it. The dashed line is where the subject was found.'
              : 'Trim the photo for the body. The original is kept.'}
          </p>
        </div>
        <div className="flex flex-col gap-3">
          {featured
            ? <SlotPreviews src={src} width={data.original.width} height={data.original.height} focal={shownFocal} box={box} fit={fit} crop={crop}
                            onFit={setFit} />
            : <CropPreview src={src} box={crop ?? WHOLE} width={data.original.width} height={data.original.height} />}
        </div>
      </div>
      {error && <p role="alert" className={errorText}>{error}</p>}
      <div className="flex flex-wrap items-center justify-end gap-2 border-t border-ink-500 pt-4">
        {savedCrop && (
          <button type="button" className={btn} disabled={busy} onClick={() => restore.mutate()}>
            {restore.isPending ? 'Restoring…' : 'Restore original'}
          </button>
        )}
        {featured && (
          <button type="button" className={btn} disabled={busy} onClick={() => reset.mutate()}>
            {reset.isPending ? 'Resetting…' : 'Reset to suggestion'}
          </button>
        )}
        <button type="button" className={btnPrimary} disabled={busy} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
    </>
  )
}
